import { CalendarPlus, CalendarRange, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  createAvailabilitySlot,
  createAvailabilitySlots,
  deleteAvailabilitySlot,
} from '@/data/availabilitySlotsRepo';
import { useCurrentUserSimple } from '@/hooks/autosave/useCurrentUserSimple';
import { useAvailabilitySlots } from '@/hooks/useAvailabilitySlots';
import {
  bulkSlotMaxDays,
  defaultDailySlotTimes,
  isSlotPast,
  planBulkSlots,
  slotTimeRangeLabel,
  validateAvailabilitySlot,
  validateBulkSlotRange,
  validateBulkSlotTimes,
  type AvailabilitySlotDraftType,
  type AvailabilitySlotType,
  type SlotTimeRangeType,
} from '@/types/types_availabilitySlot';
import { closureMessageFor, isDateClosed, type ExperienceClosuresType } from '@/types/types_closure';
import { formatCalendarDate, todayCalendarDate } from '@/util/util_format';

/**
 * The availability calendar for a 'direct' experience: the artisan's diary, typed
 * up, one row per slot the partners may take.
 *
 * Two ways in, writing the same documents. A run of days fills the artisan's
 * usual working day across a date range in one press (`BulkSlotAdder`, which is
 * how a month gets typed); the form under it adds one sitting, which is what an
 * evening the artisan has offered actually is.
 *
 * Not part of the autosaving form around it, and deliberately outside it: a slot
 * is its own document, created and deleted rather than edited, and folding it into
 * the experience's undo stack would offer to "undo" a slot a partner has since
 * booked. Adding and removing are immediate and say so.
 *
 * Editing a slot's time is not offered either, for the same reason — change it and
 * the artisan is working an hour nobody agreed to. Delete it and add the right
 * one, which is two clicks and leaves no doubt about what happened.
 *
 * `closures` are the days the experience cannot be run at all, edited just below
 * this on the same card. A slot on a closed day is a contradiction rather than an
 * override: the booking function refuses it, so one is refused here too, and any
 * that already existed when the closure was added are called out in the list.
 */
export function AvailabilitySlotsField({
  experienceId,
  closures,
}: {
  experienceId: string;
  closures: ExperienceClosuresType;
}) {
  const { slots, loading, error } = useAvailabilitySlots(experienceId);
  const currentUser = useCurrentUserSimple();

  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [saving, setSaving] = useState(false);
  // The slot whose delete is in flight, so the spinner replaces that row's bin
  // rather than every row's.
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const now = new Date().toISOString();
  // Past slots are the record of what has been run; the calendar proper is what is
  // still to come. Split rather than filtered away, because a slot booked for
  // yesterday still explains a booking in the queue.
  const upcoming = slots.filter((slot) => !isSlotPast(slot, now));
  const past = slots.filter((slot) => isSlotPast(slot, now));

  async function addSlot() {
    const draft: AvailabilitySlotDraftType = { date, startTime, endTime };
    // The same check the booking side uses, against the slots already on this
    // experience: two overlapping slots are two partners booking one artisan for the
    // same hour, which neither booking would look wrong about on its own.
    const problem = validateAvailabilitySlot(draft, slots);
    if (problem) {
      setFormError(problem);
      return;
    }
    // A time on a day the experience is closed could never be taken — the
    // booking function checks the same closures inside the transaction — so it
    // is refused here rather than saved as a slot that only looks bookable.
    const closed = closureMessageFor(draft.date, closures);
    if (closed) {
      setFormError(`${closed} Remove that closure below, or choose another day.`);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await createAvailabilitySlot(experienceId, draft, currentUser.uid);
      // Times cleared, date kept: a day is filled in a sitting at a time, and
      // retyping the date for each one is the tedious part.
      setStartTime('');
      setEndTime('');
    } catch (e) {
      console.error('Adding an availability slot failed', e);
      setFormError('Could not add that slot. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function removeSlot(slot: AvailabilitySlotType) {
    setFormError(null);
    setDeletingId(slot.id);
    try {
      await deleteAvailabilitySlot(slot.id);
    } catch (e) {
      console.error('Deleting an availability slot failed', e);
      setFormError('Could not delete that slot. Please try again.');
    } finally {
      // The row usually vanishes with the snapshot before this runs; it matters
      // on the failure path, where the button has to become pressable again.
      setDeletingId(null);
    }
  }

  return (
    <div className='tw:space-y-4'>
      <p className='tw:text-xs tw:text-muted-foreground'>
        Times are Japan Standard Time. Each slot takes exactly one booking, and disappears from the
        experience page the moment a partner takes it.
      </p>

      <BulkSlotAdder
        experienceId={experienceId}
        slots={slots}
        closures={closures}
        now={now}
        createdByUid={currentUser.uid}
      />

      <div>
        <p className='tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
          Add one slot
        </p>
        <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
          For a single sitting outside the usual day — an evening the artisan has offered, or the one
          time a run of days missed.
        </p>
      </div>

      <div className='tw:flex tw:flex-wrap tw:items-end tw:gap-3'>
        <label className='tw:block'>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            Date
          </span>
          <input
            type='date'
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className='tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-sm'
          />
        </label>
        <label className='tw:block'>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            Start
          </span>
          <input
            type='time'
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className='tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-sm'
          />
        </label>
        <label className='tw:block'>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            End
          </span>
          <input
            type='time'
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className='tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-sm'
          />
        </label>
        <Button type='button' variant='outline' loading={saving} onClick={() => void addSlot()}>
          <CalendarPlus className='tw:size-4' />
          {saving ? 'Adding…' : 'Add slot'}
        </Button>
      </div>

      {formError && <p className='tw:text-sm tw:text-destructive'>{formError}</p>}
      {error && <p className='tw:text-sm tw:text-destructive'>{error}</p>}

      {loading ? (
        <p className='tw:text-sm tw:text-muted-foreground'>Loading availability…</p>
      ) : (
        <>
          <SlotTable
            title='Upcoming'
            slots={upcoming}
            closures={closures}
            emptyNote='Nothing open. Until a slot is added, the experience page has no times to offer.'
            deletingId={deletingId}
            onDelete={(slot) => void removeSlot(slot)}
          />
          {past.length > 0 && (
            <SlotTable
              title='Past'
              slots={past}
              closures={closures}
              deletingId={deletingId}
              onDelete={(slot) => void removeSlot(slot)}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Fills a run of days with the artisan's usual working day, in one go.
 *
 * The manual form below it is the one that decides what a slot is; this is the
 * same thing repeated, and it writes ordinary slots that are deleted one at a
 * time like any other. What it adds is the arithmetic nobody should be doing by
 * hand: a month of a six-sitting day is 180 rows, and typing them is how a date
 * gets skipped.
 *
 * Nothing is stored about the run itself — no recurrence rule, no series id.
 * That is the point rather than a shortcut: the artisan's calendar is the slots,
 * so a series that could be edited afterwards would be a second version of it,
 * and the two would disagree the first time one day was moved.
 *
 * Days that cannot take a slot are skipped rather than refused, and counted
 * before the button is pressed — filling next month over a fortnight already
 * typed in is the normal way this is used, not a mistake to be stopped.
 */
function BulkSlotAdder({
  experienceId,
  slots,
  closures,
  now,
  createdByUid,
}: {
  experienceId: string;
  /** The whole calendar, so the plan can skip hours already taken. */
  slots: AvailabilitySlotType[];
  closures: ExperienceClosuresType;
  /** The clock the plan is read against, shared with the list around it. */
  now: string;
  createdByUid: string;
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [times, setTimes] = useState<SlotTimeRangeType[]>([...defaultDailySlotTimes]);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const timesProblem = validateBulkSlotTimes(times);
  // Only complained about once both ends are filled in; the first date typed is
  // not yet a range that is wrong.
  const rangeProblem = startDate && endDate ? validateBulkSlotRange(startDate, endDate) : null;

  const plan = useMemo(
    () =>
      startDate && endDate && !rangeProblem && !timesProblem
        ? planBulkSlots(startDate, endDate, times, slots, closures, now)
        : null,
    [startDate, endDate, rangeProblem, timesProblem, times, slots, closures, now],
  );

  function updateTime(index: number, patch: Partial<SlotTimeRangeType>) {
    setTimes(times.map((time, i) => (i === index ? { ...time, ...patch } : time)));
  }

  async function createSlots() {
    if (!plan || plan.create.length === 0) return;
    setCreating(true);
    setFormError(null);
    setResult(null);
    try {
      await createAvailabilitySlots(experienceId, plan.create, createdByUid);
      setResult(`Added ${countLabel(plan.create.length, 'slot')}.${describeSkipped(plan)}`);
    } catch (e) {
      console.error('Bulk-adding availability slots failed', e);
      // Deliberately not "nothing was added": the batches commit in order, so a
      // failure part way through has written whole days already. The list below
      // shows what landed, and pressing it again fills in the rest.
      setFormError('Could not add all of those slots. Check the list below, then try again.');
    } finally {
      setCreating(false);
    }
  }

  const isDefaultTimes =
    times.length === defaultDailySlotTimes.length &&
    times.every(
      (time, i) =>
        time.startTime === defaultDailySlotTimes[i].startTime &&
        time.endTime === defaultDailySlotTimes[i].endTime,
    );

  return (
    <div className='tw:space-y-3 tw:rounded-md tw:border tw:bg-muted/30 tw:p-4'>
      <div>
        <p className='tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
          Fill a run of days
        </p>
        <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
          Adds the times below to every day between the two dates, both included. Closed days are
          skipped, and so is any hour a slot already covers.
        </p>
      </div>

      <div className='tw:flex tw:flex-wrap tw:items-end tw:gap-3'>
        <label className='tw:block'>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            First day
          </span>
          <input
            type='date'
            value={startDate}
            min={todayCalendarDate()}
            onChange={(e) => setStartDate(e.target.value)}
            className='tw:rounded-md tw:border tw:bg-background tw:px-3 tw:py-2 tw:text-sm'
          />
        </label>
        <label className='tw:block'>
          <span className='tw:mb-1 tw:block tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
            Last day
          </span>
          <input
            type='date'
            value={endDate}
            min={startDate || todayCalendarDate()}
            onChange={(e) => setEndDate(e.target.value)}
            className='tw:rounded-md tw:border tw:bg-background tw:px-3 tw:py-2 tw:text-sm'
          />
        </label>
      </div>

      <div className='tw:space-y-2'>
        <p className='tw:text-xs tw:tracking-wider tw:text-muted-foreground tw:uppercase'>
          Times each day
        </p>
        <div className='tw:flex tw:flex-wrap tw:gap-2'>
          {times.map((time, index) => (
            <div
              // Position rather than the times themselves: these are being typed,
              // and a half-finished time is not unique.
              key={index}
              className='tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:border tw:bg-background tw:px-2 tw:py-1'
            >
              <input
                type='time'
                value={time.startTime}
                aria-label={`Start of daily time ${index + 1}`}
                onChange={(e) => updateTime(index, { startTime: e.target.value })}
                className='tw:w-24 tw:text-sm'
              />
              <span className='tw:text-muted-foreground'>–</span>
              <input
                type='time'
                value={time.endTime}
                aria-label={`End of daily time ${index + 1}`}
                onChange={(e) => updateTime(index, { endTime: e.target.value })}
                className='tw:w-24 tw:text-sm'
              />
              <button
                type='button'
                onClick={() => setTimes(times.filter((_, i) => i !== index))}
                aria-label={`Remove the ${slotTimeRangeLabel(time) || `${index + 1}`} time`}
                className='tw:text-muted-foreground tw:transition-colors tw:hover:text-destructive'
              >
                <Trash2 className='tw:size-3.5' />
              </button>
            </div>
          ))}
        </div>
        <div className='tw:flex tw:flex-wrap tw:gap-2'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => setTimes([...times, { startTime: '', endTime: '' }])}
          >
            <Plus className='tw:size-4' />
            Add a time
          </Button>
          {!isDefaultTimes && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setTimes([...defaultDailySlotTimes])}
            >
              <RotateCcw className='tw:size-4' />
              Reset to the usual day
            </Button>
          )}
        </div>
      </div>

      {timesProblem && <p className='tw:text-sm tw:text-destructive'>{timesProblem}</p>}
      {rangeProblem && <p className='tw:text-sm tw:text-destructive'>{rangeProblem}</p>}
      {formError && <p className='tw:text-sm tw:text-destructive'>{formError}</p>}
      {result && <p className='tw:text-sm tw:text-muted-foreground'>{result}</p>}

      <div className='tw:flex tw:flex-wrap tw:items-center tw:gap-3'>
        <Button
          type='button'
          variant='outline'
          loading={creating}
          disabled={!plan || plan.create.length === 0}
          onClick={() => void createSlots()}
        >
          <CalendarRange className='tw:size-4' />
          {creating
            ? 'Adding…'
            : plan && plan.create.length > 0
              ? `Add ${countLabel(plan.create.length, 'slot')}`
              : 'Add slots'}
        </Button>
        {plan && (
          <p className='tw:text-xs tw:text-muted-foreground'>
            {plan.create.length > 0
              ? `${countLabel(plan.dayCount, 'day')} selected.${describeSkipped(plan)}`
              : 'Nothing to add — every time in that range is already covered, closed or past.'}
          </p>
        )}
      </div>

      <p className='tw:text-xs tw:text-muted-foreground'>
        Up to {bulkSlotMaxDays} days at a time. Each slot is added on its own and is deleted from the
        list below one at a time — there is no way to undo a whole run.
      </p>
    </div>
  );
}

/** "1 slot" / "12 slots". */
function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * What a plan leaves out, as a sentence to hang off the end of another one, or
 * '' when it leaves nothing out.
 *
 * Said in the form's own terms — closed, already covered, already started —
 * because "skipped 14" invites the admin to go looking for a mistake that is
 * not there.
 */
function describeSkipped(plan: {
  closedDates: string[];
  clashCount: number;
  pastCount: number;
}): string {
  const parts: string[] = [];
  if (plan.closedDates.length > 0) parts.push(`${countLabel(plan.closedDates.length, 'day')} closed`);
  if (plan.clashCount > 0) parts.push(`${countLabel(plan.clashCount, 'slot')} already covered`);
  if (plan.pastCount > 0) parts.push(`${countLabel(plan.pastCount, 'slot')} already started`);
  return parts.length > 0 ? ` Skipped: ${parts.join(', ')}.` : '';
}

/**
 * One list of slots, with what became of each.
 *
 * A booked slot has no delete button, and that is the rules' decision rather than
 * this component's — it holds a booking a partner has been told about, and the
 * document is what ties the two together. Cancelling the booking is what releases
 * it, from the requests queue, which is where the fee and the emails are decided.
 */
function SlotTable({
  title,
  slots,
  closures,
  emptyNote,
  deletingId,
  onDelete,
}: {
  title: string;
  slots: AvailabilitySlotType[];
  closures: ExperienceClosuresType;
  emptyNote?: string;
  /** The slot currently being deleted, if it is one of these. */
  deletingId: string | null;
  onDelete: (slot: AvailabilitySlotType) => void;
}) {
  return (
    <div>
      <p className='tw:mb-2 tw:text-xs tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase'>
        {title} <span className='tw:font-normal'>({slots.length})</span>
      </p>
      {slots.length === 0 ? (
        <p className='tw:text-sm tw:text-muted-foreground'>{emptyNote}</p>
      ) : (
        <ul className='tw:divide-y tw:rounded-md tw:border'>
          {slots.map((slot) => (
            <li key={slot.id} className='tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:text-sm'>
              <span className='tw:w-44 tw:shrink-0'>{formatCalendarDate(slot.date)}</span>
              <span className='tw:w-28 tw:shrink-0 tw:text-muted-foreground'>{slotTimeRangeLabel(slot)}</span>
              {slot.status === 'booked' ? (
                <>
                  <span className='tw:rounded-full tw:bg-green-100 tw:px-2 tw:py-0.5 tw:text-xs tw:text-green-800'>
                    Booked
                  </span>
                  <Link
                    to='/admin/requests'
                    className='tw:text-xs tw:text-muted-foreground tw:underline tw:hover:text-foreground'
                  >
                    {slot.bookingRequestId?.slice(0, 8)}
                  </Link>
                </>
              ) : (
                <span className='tw:text-xs tw:text-muted-foreground'>Open</span>
              )}
              {/* A slot cannot be added on a closed day, so this is a closure
                  added after the fact. The slot is not offered on the experience
                  page and could not be booked if it were — said here because
                  this is the list where it looks like availability. */}
              {isDateClosed(slot.date, closures) && (
                <span className='tw:rounded-full tw:bg-destructive/10 tw:px-2 tw:py-0.5 tw:text-xs tw:text-destructive'>
                  Closed day — not offered
                </span>
              )}
              <span className='tw:flex-1' />
              {slot.status === 'open' && (
                <button
                  type='button'
                  onClick={() => onDelete(slot)}
                  disabled={deletingId === slot.id}
                  aria-busy={deletingId === slot.id || undefined}
                  aria-label={`Delete the slot on ${slot.date} at ${slot.startTime}`}
                  className='tw:text-muted-foreground tw:transition-colors tw:hover:text-destructive tw:disabled:hover:text-muted-foreground'
                >
                  {deletingId === slot.id ? (
                    <Loader2 className='tw:size-4 tw:animate-spin' />
                  ) : (
                    <Trash2 className='tw:size-4' />
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
